import React, { useContext } from "react";
import { flatMap, map } from "lodash";
import { useHistory, useLocation, useRouteMatch } from "react-router";
import { FEATURE_FLAG_NAME } from "common/constants";
import { buildLink } from "common/routing";
import { isNotNil } from "common/utils/tools";
import {
  getInfoPanelLinkForAttached,
  getObjectDisplayForAttached,
} from "common-client/utils/customObject";
import { useGetSubmissionWithSummariesQuery } from "../../generated/graphql";
import { AuthContext } from "../Authorization/AuthContext";
import FullPageFormLayout from "../Common/FullPageFormLayout";
import { ButtonContainer, Stack } from "./__styles__/Submission";
// move these shared styles out of create submission style file
import ActionButton from "./ActionButton";
import { MODULE_CONFIGURATIONS } from "./modules";
import SubmissionHeader from "./SubmissionHeader";
import { hasSummary } from "./utils";

const SubmissionSummary = ({
  moduleConfigurations = MODULE_CONFIGURATIONS,
}: {
  moduleConfigurations?: typeof MODULE_CONFIGURATIONS;
}) => {
  const routeMatch = useRouteMatch<{ submissionId: string }>();
  const submissionId = routeMatch.params.submissionId;

  const history = useHistory();
  const location = useLocation<Maybe<{ prevLocation?: string }>>();
  const prevLocation = location.state?.prevLocation || "/map";
  const { account, isPublic, isFeatureEnabled, publicUser } =
    useContext(AuthContext);
  const isGuest = isPublic && !publicUser;

  const { data, loading } = useGetSubmissionWithSummariesQuery({
    variables: { submissionId, isPublic, isGuest },
    fetchPolicy: "no-cache",
    onCompleted: data => {
      // if the submission doesn't have any summary components, redirect to the edit page
      // as the user probably got sent here by mistake somehow
      if (!data.submission?.summaries.length) {
        history.replace({
          pathname: buildLink("editSubmission", { submissionId }),
          state: { prevLocation },
        });
      }
    },
  });

  if (!data?.submission || loading) return <div>Loading...</div>;

  const submission = data.submission;
  const submissionType = submission.submissionTypeVersion.submissionType;
  const modules = submissionType.modules;
  const property = submission.property;
  const submissionTypes = account?.submissionTypes ?? [];

  const summaryComponents = map(
    modules,
    module => moduleConfigurations[module].summaryComponent,
  ).filter(isNotNil);

  const additionalActions = flatMap(modules, module =>
    moduleConfigurations[module].buildActions({
      submission,
      property,
      submissionTypes,
    }),
  );

  const rightContainer = (
    <ButtonContainer>
      <ActionButton
        isSummaryView={true}
        additionalActions={additionalActions}
        submission={{
          ...submission,
          category: submissionType.category,
          hasSummary: hasSummary(submission),
        }}
        objectIsHiddenFromPublic={submission.property?.hiddenFromPublic}
      />
    </ButtonContainer>
  );

  return (
    <FullPageFormLayout
      subtitle={submissionType.name}
      prevLocation={prevLocation}
      rightContainer={rightContainer}
      width="wide"
      buttonLoading={loading}
    >
      <Stack main>
        <SubmissionHeader
          submissionType={submissionType}
          account={{
            logoUrl: account?.logoUrl ?? "",
            name: account?.name ?? "",
          }}
          objectDisplay={getObjectDisplayForAttached(submission)}
          objectInfoPanelLink={getInfoPanelLinkForAttached({
            ...submission,
            isPublic,
            isPublicCustomObjectsEnabled: isFeatureEnabled(
              FEATURE_FLAG_NAME.PUBLIC_CUSTOM_OBJECTS,
            ),
          })}
          viewers={submission.viewers}
        />
        <div>
          {summaryComponents.map((Component, index) => (
            <Component key={index} submission={submission} />
          ))}
        </div>
      </Stack>
    </FullPageFormLayout>
  );
};

export default SubmissionSummary;
